import { useEffect, useState } from "react";
import axios from "axios";
import { Building2, Check, Pencil, CalendarDays } from "lucide-react";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { COURT_TYPE_LABELS, branchDisplay, workspaceLabel } from "../utils/workspace";

const ALL = "__all__";
const NONE = "__none__";

/** Επιλογή/επιβεβαίωση Καταστήματος + Τμήματος + Έτους (Πίνακας Ελέγχου) — τα δεδομένα φορτώνουν ΜΟΝΟ μετά την επιβεβαίωση.
 *  user (Νοέ 2026): και τα τρία πεδία είναι υποχρεωτικά. */
export const WorkspaceGate = ({ user, initial, onConfirm }) => {
  const [branches, setBranches] = useState([]);
  const [years, setYears] = useState([]);
  const [branch, setBranch] = useState(initial?.branch || user?.court_branch || NONE);
  const [courtType, setCourtType] = useState(initial?.court_type ? initial.court_type : initial?.confirmed_at ? ALL : NONE);
  const [year, setYear] = useState(initial?.year || NONE);
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    axios.get(`${API}/branches`).then((r) => {
      const list = r.data?.branches || [];
      setBranches(list);
      if (!initial?.branch) {
        const mine = list.find((b) => user?.court_branch && (user.court_branch.includes(b.label) || b.label.includes(user.court_branch)));
        if (mine) setBranch(mine.label);
        else if (list.length === 1) setBranch(list[0].label);
      }
      if (!initial?.court_type && userCourtTypes.length === 1) setCourtType(userCourtTypes[0]);
    }).catch(() => {});
    axios.get(`${API}/case-years`).then((r) => {
      const ys = (r.data?.years || []).map(String);
      setYears(ys);
      if (!initial?.year && r.data?.current) setYear(String(r.data.current));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const courtOptions = isAdmin ? ["Μονομελές", "Πολυμελές"] : userCourtTypes;
  const ready = branch !== NONE && courtType !== NONE && year !== NONE && !!year;
  return (
    <Card className="border-blue-200 bg-blue-50/40" data-testid="workspace-gate">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Building2 className="w-5 h-5 text-blue-700" />Επιλογή Καταστήματος, Τμήματος &amp; Έτους</CardTitle>
        <CardDescription>Επιλέξτε υποχρεωτικά Κατάστημα, Τμήμα και Έτος (ΓΑΚ), ώστε να φορτωθούν μόνο τα δεδομένα που σας αφορούν (ταχύτερη λειτουργία).</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs">Κατάστημα *</Label>
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger className="w-72 h-9 bg-white" data-testid="workspace-branch-select"><SelectValue placeholder="Επιλέξτε κατάστημα" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE} disabled>Επιλέξτε κατάστημα</SelectItem>
              {isAdmin && <SelectItem value={ALL} data-testid="workspace-branch-all">Όλα τα Καταστήματα</SelectItem>}
              {branches.map((b) => <SelectItem key={b.label} value={b.label} data-testid={`workspace-branch-${b.label}`}>{b.display || branchDisplay(b.label, b.is_regional)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Τμήμα / Τύπος *</Label>
          <Select value={courtType} onValueChange={setCourtType}>
            <SelectTrigger className="w-64 h-9 bg-white" data-testid="workspace-court-type-select"><SelectValue placeholder="Επιλέξτε τμήμα" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE} disabled>Επιλέξτε τμήμα</SelectItem>
              {(isAdmin || courtOptions.length > 1) && <SelectItem value={ALL} data-testid="workspace-court-type-all">Όλα τα Τμήματα</SelectItem>}
              {courtOptions.map((ct) => <SelectItem key={ct} value={ct} data-testid={`workspace-court-type-${ct}`}>{COURT_TYPE_LABELS[ct] || ct}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1"><CalendarDays className="w-3 h-3" />Έτος (ΓΑΚ) *</Label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40 h-9 bg-white" data-testid="workspace-year-select"><SelectValue placeholder="Επιλέξτε έτος" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE} disabled>Επιλέξτε έτος</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y} data-testid={`workspace-year-${y}`}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => onConfirm({ branch: branch === ALL ? "" : branch, court_type: courtType === ALL ? "" : courtType, year })}
                className="h-9" disabled={!ready} data-testid="workspace-confirm-btn">
          <Check className="w-4 h-4 mr-1" />Επιβεβαίωση &amp; Φόρτωση
        </Button>
        {!ready && <span className="text-xs text-slate-500" data-testid="workspace-required-hint">Συμπληρώστε και τα τρία πεδία για να συνεχίσετε.</span>}
      </CardContent>
    </Card>
  );
};

/** Μικρή ένδειξη ενεργού Χώρου Εργασίας με κουμπί αλλαγής. */
export const WorkspaceBadge = ({ ws, onChange }) => (
  <div className="flex items-center gap-2 text-xs bg-slate-100 border border-slate-200 rounded-lg px-3 py-1.5" data-testid="workspace-badge">
    <Building2 className="w-3.5 h-3.5 text-slate-500" />
    <span className="font-medium text-slate-700" data-testid="workspace-label">{workspaceLabel(ws)}</span>
    {onChange && (
      <button type="button" onClick={onChange} className="text-blue-700 hover:underline flex items-center gap-1" data-testid="workspace-change-btn">
        <Pencil className="w-3 h-3" />Αλλαγή
      </button>
    )}
  </div>
);

/** Dropdown «Κατάστημα» για φίλτρα λιστών/στατιστικών/αναφορών (value "" = όλα). */
export const BranchFilterSelect = ({ value, onChange, className = "w-full md:w-60", testId = "branch-filter", disabled = false }) => {
  const [branches, setBranches] = useState([]);
  useEffect(() => { axios.get(`${API}/branches`).then((r) => setBranches(r.data?.branches || [])).catch(() => {}); }, []);
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)} disabled={disabled}>
      <SelectTrigger className={className} data-testid={testId}><SelectValue placeholder="Κατάστημα" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Κατάστημα: Όλα</SelectItem>
        {branches.map((b) => <SelectItem key={b.label} value={b.label}>{b.display || branchDisplay(b.label, b.is_regional)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};
